import {
  BillingSettingsAction,
  CommentAction,
  MasterRuleAction,
  MasterRuleBookAction,
  RuleAction,
  RuleBookAction,
} from "actions";
import { MLASupplementaryDataAction } from "actions/mlaSupplementaryData";
import { XeroTrackingCategoryAction } from "actions/xeroTrackingCategories";

import {
  getActiveLivestockAgentDeployment,
  getActiveSaleyardAdminSaleyard,
  getIsFetchingBillingSettings,
  getIsFetchingSideLoadedBusinesses,
  getIsFetchingSideLoadedDataSets,
  getIsFetchingBusinessRelations,
  getIsFetchingComments,
  getIsFetchingDefaultVendorSplits,
  getIsFetchingDeployments,
  getIsFetchingMasterRuleBooks,
  getIsFetchingMasterRules,
  getIsFetchingMasterRuleSets,
  getIsFetchingMLASupplementaryData,
  getIsFetchingRuleBooks,
  getIsFetchingRules,
  getIsFetchingRuleSets,
  getIsFetchingSellViaExAgentRebates,
  getIsFetchingSundryTemplates,
  getIsFetchingTradingTerms,
  getIsFetchingXeroTrackingCategories,
  getIsLivestockAgent,
  getIsSaleyardAdmin,
  selectCurrentSaleyard,
  getIsFetchingWeighLots,
  getIsFetchingWeighLotScans,
  getIsFetchingPenScanLots,
  getIsFetchingReceivalLots,
} from "selectors";
import { hasPermission } from "lib/permissions";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";
import { TradingTermAction } from "actions/tradingTerms";
import { SellViaExAgentRebateAction } from "actions/sellViaExAgentRebates";
import { SundryTemplateAction } from "actions/sundryTemplates";
import { InterestSettingsAction } from "actions/interest";
import { DefaultVendorSplitAction } from "actions/vendorSplits";
import { BusinessRelationAction } from "actions/businessRelation";
import { Action } from "redux";
import { MasterRuleSetAction, RuleSetAction } from "actions/ruleSets";
import {
  SideLoadedBusinessAction,
  SideLoadedDataSetAction,
} from "actions/sideLoadedData";
import { WeighLotAction } from "actions/weighLots";
import { WeighLotScanAction } from "actions/weighLotScans";
import { PenScanLotAction } from "actions/penScanLots";
import { ReceivalLotAction } from "actions/receivalLots";

interface IActionFilter {
  (
    /**
     * Redux application state
     */
    state: State,
    /**
     * Whether we need a 'changes since' action
     */
    changes?: boolean,
  ): /**
   * Returns the action to run, if filters allow it, otherwise null
   */
  Action | null;
}

const checkPermission = function (
  state: State,
  deploymentPermission?: string,
  saleyardPermission?: string,
): boolean {
  const deployment = getActiveLivestockAgentDeployment(state);
  const saleyard = getActiveSaleyardAdminSaleyard(state);
  const isSaleyardAdmin = getIsSaleyardAdmin(state);
  const isLivestockAgent = getIsLivestockAgent(state);

  const hasLivestockAgentPermission =
    deploymentPermission && isLivestockAgent
      ? hasPermission(deployment, deploymentPermission)
      : false;

  const hasSaleyardAdminPermission =
    saleyardPermission && isSaleyardAdmin
      ? hasPermission(saleyard, saleyardPermission)
      : false;

  return hasLivestockAgentPermission || hasSaleyardAdminPermission;
};

const businessRelationActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  const enabled: boolean = checkPermission(
    state,
    DeploymentPermission.featureBusinessRelations,
  );

  if (!enabled) {
    return null;
  }
  if (!changes) {
    return BusinessRelationAction.request();
  }
  if (changes && !getIsFetchingBusinessRelations(state)) {
    return BusinessRelationAction.requestChanges({
      changesSince: state.businessRelations.lastModifiedTimestamp,
    });
  }
  // we requested changes but are fetching already.
  return null;
};

const commentActionFilter: IActionFilter = function (state, changes = false) {
  const enabled = getIsSaleyardAdmin(state) || getIsLivestockAgent(state);

  if (!enabled) {
    return null;
  }
  if (!changes) {
    return CommentAction.request();
  }
  if (changes && !getIsFetchingComments(state)) {
    return CommentAction.requestChanges({
      changesSince: state.comments.lastModifiedTimestamp,
    });
  }
  // we requested changes but are fetching already.
  return null;
};

const mlaSupplementaryDataActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  const enabled: boolean =
    getIsSaleyardAdmin(state) &&
    hasPermission(
      selectCurrentSaleyard(state),
      SaleyardPermission.featureMLASupplementaryData,
    );

  if (enabled) {
    if (!changes) {
      return MLASupplementaryDataAction.request();
    }
    if (changes && !getIsFetchingMLASupplementaryData(state)) {
      return MLASupplementaryDataAction.requestChanges({
        changesSince: state.mlaSupplementaryData.lastModifiedTimestamp,
      });
    }
  }
  return null;
};

const tradingTermActionActionFilter: IActionFilter = function (state, changes) {
  if (
    checkPermission(
      state,
      DeploymentPermission.featureTradingTerms,
      SaleyardPermission.featureTradingTerms,
    ) &&
    !getIsFetchingTradingTerms(state)
  ) {
    return changes
      ? TradingTermAction.requestChanges({
          changesSince: state.tradingTerms.lastModifiedTimestamp,
        })
      : TradingTermAction.request();
  }
  return null;
};

const sellViaExAgentRebatesActionFilter: IActionFilter = function (
  state,
  changes,
) {
  if (
    checkPermission(
      state,
      DeploymentPermission.featureSellViaExAgentRebates,
      SaleyardPermission.featureSellViaExAgentRebates,
    ) &&
    !getIsFetchingSellViaExAgentRebates(state)
  ) {
    return changes
      ? SellViaExAgentRebateAction.requestChanges({
          changesSince: state.sellViaExAgentRebates.lastModifiedTimestamp,
        })
      : SellViaExAgentRebateAction.request();
  }
  return null;
};

const interestSettingsActionActionFilter: IActionFilter = function (
  state,
  changes: false,
) {
  const hasInterestPermission = checkPermission(
    state,
    DeploymentPermission.featureInterest,
  );
  if (!hasInterestPermission) {
    return null;
  }
  if (!changes) {
    return InterestSettingsAction.request();
  }
  if (changes && !getIsFetchingDeployments(state)) {
    return InterestSettingsAction.requestChanges({
      changesSince: state.interestSettings.lastModifiedTimestamp,
    });
  }
  // we requested changes but are fetching already.
  return null;
};

const defaultVendorSplitActionActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  const hasVendorSplitPermission = checkPermission(
    state,
    DeploymentPermission.featureVendorSplits,
  );
  if (!hasVendorSplitPermission) {
    return null;
  }
  if (!changes) {
    return DefaultVendorSplitAction.request();
  }
  if (changes && !getIsFetchingDefaultVendorSplits(state)) {
    return DefaultVendorSplitAction.requestChanges({
      changesSince: state.vendorSplits.default.lastModifiedTimestamp,
    });
  }
  // we requested changes but are fetching already.
  return null;
};

const sundryTemplateActionActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  if (
    checkPermission(
      state,
      DeploymentPermission.featureSundryTemplates,
      SaleyardPermission.featureSundryTemplates,
    ) &&
    !getIsFetchingSundryTemplates(state)
  ) {
    return changes
      ? SundryTemplateAction.requestChanges({
          changesSince: state.sundryTemplates.lastModifiedTimestamp,
        })
      : SundryTemplateAction.request();
  }
  return null;
};

const masterRuleBooksActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  let action: Action = null;
  if (
    checkPermission(
      state,
      DeploymentPermission.featureRuleBook,
      SaleyardPermission.featureRuleBook,
    ) &&
    !getIsFetchingMasterRuleBooks(state)
  ) {
    action = changes
      ? MasterRuleBookAction.requestChanges({
          changesSince: state.masterRuleBooks.lastModifiedTimestamp,
        })
      : MasterRuleBookAction.request();
  }

  return action;
};

const masterRulesActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  if (
    checkPermission(
      state,
      DeploymentPermission.featureRuleBook,
      SaleyardPermission.featureRuleBook,
    ) &&
    !getIsFetchingMasterRules(state)
  ) {
    return changes
      ? MasterRuleAction.requestChanges({
          changesSince: state.masterRules.lastModifiedTimestamp,
        })
      : MasterRuleAction.request();
  }
  return null;
};

const ruleBooksActionFilter: IActionFilter = function (state, changes = false) {
  if (
    checkPermission(
      state,
      DeploymentPermission.featureRuleBook,
      SaleyardPermission.featureRuleBook,
    ) &&
    !getIsFetchingRuleBooks(state)
  ) {
    return changes
      ? RuleBookAction.requestChanges({
          changesSince: state.ruleBooks.lastModifiedTimestamp,
        })
      : RuleBookAction.request();
  }
  return null;
};

const rulesActionFilter: IActionFilter = function (state, changes = false) {
  if (
    checkPermission(
      state,
      DeploymentPermission.featureRuleBook,
      SaleyardPermission.featureRuleBook,
    ) &&
    !getIsFetchingRules(state)
  ) {
    return changes
      ? RuleAction.requestChanges({
          changesSince: state.rules.lastModifiedTimestamp,
        })
      : RuleAction.request();
  }

  return null;
};

const rulesSetActionFilter: IActionFilter = function (state, changes = false) {
  if (
    checkPermission(
      state,
      DeploymentPermission.featureRuleBook,
      SaleyardPermission.featureRuleBook,
    ) &&
    !getIsFetchingRuleSets(state)
  ) {
    return changes
      ? RuleSetAction.requestChanges({
          changesSince: state.ruleSets.lastModifiedTimestamp,
        })
      : RuleSetAction.request();
  }
  return null;
};

const masterRulesSetActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  if (
    checkPermission(
      state,
      DeploymentPermission.featureRuleBook,
      SaleyardPermission.featureRuleBook,
    ) &&
    !getIsFetchingMasterRuleSets(state)
  ) {
    return changes
      ? MasterRuleSetAction.requestChanges({
          changesSince: state.masterRuleSets.lastModifiedTimestamp,
        })
      : MasterRuleSetAction.request();
  }

  return null;
};

const xeroTrackingCategoryActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  if (
    checkPermission(
      state,
      DeploymentPermission.featureTrackingCategories,
      SaleyardPermission.featureTrackingCategories,
    ) &&
    !getIsFetchingXeroTrackingCategories(state)
  ) {
    return changes
      ? XeroTrackingCategoryAction.requestChanges({
          changesSince: state.xeroTrackingCategories.lastModifiedTimestamp,
        })
      : XeroTrackingCategoryAction.request();
  }
  return null;
};

const billingSettingsActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  const enabled = checkPermission(
    state,
    DeploymentPermission.featureBillingSettings,
    SaleyardPermission.featureBillingSettings,
  );

  if (!enabled) {
    return null;
  }
  if (!changes) {
    return BillingSettingsAction.request();
  }
  if (changes && !getIsFetchingBillingSettings(state)) {
    return BillingSettingsAction.requestChanges({
      changesSince: state.billingSettings.lastModifiedTimestamp,
    });
  }
  // we requested changes but are fetching already.
  return null;
};

const sideLoadedBusinessActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  const enabled = checkPermission(
    state,
    DeploymentPermission.featureSideLoadedDataSets,
    SaleyardPermission.featureSideLoadedDataSets,
  );
  if (!enabled) {
    return null;
  }
  if (!changes) {
    return SideLoadedBusinessAction.request();
  }
  if (changes && !getIsFetchingSideLoadedBusinesses(state)) {
    return SideLoadedBusinessAction.requestChanges({
      changesSince: state.sideLoadedBusinesses.lastModifiedTimestamp,
    });
  }
  return null;
};

const sideLoadedDataSetActionFilter: IActionFilter = function (
  state,
  changes = false,
) {
  const enabled = checkPermission(
    state,
    DeploymentPermission.featureSideLoadedDataManagement,
    SaleyardPermission.featureSideLoadedDataManagement,
  );
  if (!enabled) {
    return null;
  }
  if (!changes) {
    return SideLoadedDataSetAction.request();
  }
  if (changes && !getIsFetchingSideLoadedDataSets(state)) {
    return SideLoadedDataSetAction.requestChanges({
      changesSince: state.sideLoadedDataSets.lastModifiedTimestamp,
    });
  }
  return null;
};

const weighLotActionFilter: IActionFilter = function (
  state: TransitionalState,
  changes = false,
) {
  const enabled: boolean =
    getIsSaleyardAdmin(state) &&
    hasPermission(
      selectCurrentSaleyard(state),
      SaleyardPermission.featureWeighLots,
    );

  if (enabled) {
    if (!changes) {
      return WeighLotAction.request();
    }
    if (changes && !getIsFetchingWeighLots(state)) {
      return WeighLotAction.requestChanges({
        changesSince: state.weighLots.lastModifiedTimestamp,
      });
    }
  }
  return null;
};

const weighLotScanActionFilter: IActionFilter = function (
  state: TransitionalState,
  changes = false,
) {
  const enabled: boolean =
    getIsSaleyardAdmin(state) &&
    hasPermission(
      selectCurrentSaleyard(state),
      SaleyardPermission.featureWeighLots,
    );

  if (enabled) {
    if (!changes) {
      return WeighLotScanAction.request();
    }
    if (changes && !getIsFetchingWeighLotScans(state)) {
      return WeighLotScanAction.requestChanges({
        changesSince: state.weighLotScans.lastModifiedTimestamp,
      });
    }
  }
  return null;
};

const penScanLotActionFilter: IActionFilter = function (
  state: TransitionalState,
  changes = false,
) {
  const enabled: boolean =
    getIsSaleyardAdmin(state) &&
    hasPermission(
      selectCurrentSaleyard(state),
      SaleyardPermission.featurePenScanLots,
    );

  if (enabled) {
    if (!changes) {
      return PenScanLotAction.request();
    }
    if (changes && !getIsFetchingPenScanLots(state)) {
      return PenScanLotAction.requestChanges({
        changesSince: state.penScanLots.lastModifiedTimestamp,
      });
    }
  }
  return null;
};

const receivalLotActionFilter: IActionFilter = function (
  state: TransitionalState,
  changes = false,
) {
  const enabled: boolean =
    getIsSaleyardAdmin(state) &&
    hasPermission(
      selectCurrentSaleyard(state),
      SaleyardPermission.featureReceivalLots,
    );

  if (enabled) {
    if (!changes) {
      return ReceivalLotAction.request();
    }
    if (changes && !getIsFetchingReceivalLots(state)) {
      return ReceivalLotAction.requestChanges({
        changesSince: state.receivalLots.lastModifiedTimestamp,
      });
    }
  }
  return null;
};

export const inSalePermissionCheckActionTypes = [
  CommentAction,
  MLASupplementaryDataAction,
  WeighLotAction,
  WeighLotScanAction,
  PenScanLotAction,
  ReceivalLotAction,
];

export const deploymentPermissionCheckActionTypes = [
  DefaultVendorSplitAction,
  InterestSettingsAction,
];

export const ruleActionTypes = [
  MasterRuleAction,
  MasterRuleBookAction,
  RuleAction,
  RuleBookAction,
  RuleSetAction,
  MasterRuleSetAction,
];

export const saleyardAndDeploymentPermissionCheckActionTypes = [
  TradingTermAction,
  SellViaExAgentRebateAction,
  SundryTemplateAction,
  SideLoadedBusinessAction,
  SideLoadedDataSetAction,
  XeroTrackingCategoryAction,
].concat(ruleActionTypes);

export const actionFilters: { [actionDataType: string]: IActionFilter } = {
  [BusinessRelationAction.dataType]: businessRelationActionFilter,
  [BillingSettingsAction.dataType]: billingSettingsActionFilter,
  [CommentAction.dataType]: commentActionFilter,
  [DefaultVendorSplitAction.dataType]: defaultVendorSplitActionActionFilter,
  [InterestSettingsAction.dataType]: interestSettingsActionActionFilter,
  [MasterRuleAction.dataType]: masterRulesActionFilter,
  [MasterRuleBookAction.dataType]: masterRuleBooksActionFilter,
  [MLASupplementaryDataAction.dataType]: mlaSupplementaryDataActionFilter,
  [RuleAction.dataType]: rulesActionFilter,
  [RuleBookAction.dataType]: ruleBooksActionFilter,
  [SundryTemplateAction.dataType]: sundryTemplateActionActionFilter,
  [TradingTermAction.dataType]: tradingTermActionActionFilter,
  [SellViaExAgentRebateAction.dataType]: sellViaExAgentRebatesActionFilter,
  [RuleSetAction.dataType]: rulesSetActionFilter,
  [MasterRuleSetAction.dataType]: masterRulesSetActionFilter,
  [SideLoadedBusinessAction.dataType]: sideLoadedBusinessActionFilter,
  [SideLoadedDataSetAction.dataType]: sideLoadedDataSetActionFilter,
  [XeroTrackingCategoryAction.dataType]: xeroTrackingCategoryActionFilter,
  [WeighLotAction.dataType]: weighLotActionFilter,
  [WeighLotScanAction.dataType]: weighLotScanActionFilter,
  [PenScanLotAction.dataType]: penScanLotActionFilter,
  [ReceivalLotAction.dataType]: receivalLotActionFilter,
};
